

import React, { useState, useRef, useContext, memo, useMemo, useEffect } from 'react';
import _get from 'lodash.get';
import styled from 'styled-components';
import { Icon } from '../../../../../../../ui-kit';
import { useEditModeManager } from '../../../../../../../common/hooks';
import editModeManager from '../../../../../../../common/utils/EditModeManager';

import { dataAdapter } from '../../../../contexts/dataAdapter';
import { OptionsBlock } from '../Actions';
import { ActionTypes, ModuleType, ImageSizes, ImageSizeTypes } from '../../../../types';

import { getImageSize, getImageSizeType } from '../../../../utils/getImageSize';
import { Visible, Edit, AnchorLink, Size, Trash } from '../Actions/components/OptionsList';
import { RelativeBlock, ClickableDiv, ImageBase } from './components/common/StyledEditables';
import { DEFAULT_LOGO } from '../../../../constants';

import getAssetType from '../../../../utils/getAssetType';
import useOuterClick from '../../../../hooks/useOuterClick';

const StyledIcon = styled(Icon)`
  justify-content: unset;
`;

const LoaderWrapper = styled.div.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;



















const Image = (props) => {
  const {
    image,
    style,
    computedStyle,
    moduleId,
    rowId,
    columnId,
    isEditable,
    isVisible,
    isImageUploader,
    isLinkEditable,
    handleTemplateChange,
    canUploadVideoImage,
    isResizable,
    hideVisibleOption,
    isWorkspaceAdminLinkEditable
  } = props;
  const { source = '', alt = '', assetId = '', imageLink = '' } = image;

  const {
    assetsMap,
    isInternalAdmin,
    removeAsset
  } = useContext(dataAdapter);

  const [canEdit, setShowEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedAssetId, setUploadedAssetId] = useState(assetId);
  const [isImageLinkEditVisible, setImageLinkEditVisible] = useState(false);
  const [inputData, setInputData] = useState(null);
  const [imageType, setImageType] = useState(''); // State for image is Square or Rectangle
  const [isSmallImageSize, setSmallImageSize] = useState(false);
  const [isMediumImageSize, setMediumImageSize] = useState(false);
  const [isLargeImageSize, setLargeImageSize] = useState(false);

  const componentId = useMemo(() => `image-${moduleId || 'unknown'}-${rowId || 'unknown'}-${columnId || 'unknown'}`, [moduleId, rowId, columnId]);

  const actionsRef = useRef();
  const uploaderRef = useRef();

  const filePickerRefCallBack = (ref) => {
    uploaderRef.current = ref;
  };

  const hideEdit = () => {
    editModeManager.clearActiveComponent();
    setShowEdit(false);
  };

  const showEdit = () => {
    editModeManager.setActiveComponent(componentId);
    setShowEdit(true);
  };

  const hideImageLinkEdit = () => {
    setImageLinkEditVisible(false);
  };

  const showImageLinkEdit = () => {
    setImageLinkEditVisible(true);

    setInputData({
      placeholder: 'Enter hyperlink',
      value: imageLink,
      isRequired: false,
      isLink: true,
      style: {
        position: 'absolute',
        top: '60px',
        zIndex: '9'
      }
    });
  };

  const handleOuterClick = () => {
    if (!canEdit) {
      return;
    }

    hideEdit();
  };

  useOuterClick({ dependencies: [canEdit], callback: handleOuterClick, innerRef: actionsRef });

  useEffect(() => {
    const asset = assetsMap.get(uploadedAssetId);
    if (asset && asset.loadedPercent === 100 && asset.uploadStatus !== 'active') {
      const assetType = getAssetType(asset.metaData);
      const descriptor = assetType === 'image' ? 'image' : 'video';

      // Delete asset from assetMap
      removeAsset(uploadedAssetId);

      handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
        data: {
          moduleDescriptorValueToSet: {
            [descriptor]: {
              source: _get(asset, 'assetFiles.ORIGINAL.sourceUrl', ''),
              assetId: uploadedAssetId
            },
            ...(canUploadVideoImage && assetType === 'image' ? {
              style: {
                width: '100%'
              }
            } : {})
          },
          overrides: {
            type: assetType === 'image' ? ModuleType.IMAGE : ModuleType.VIDEO
          }
        }
      });
    }
  }, [uploadedAssetId, assetsMap, canUploadVideoImage]);

  useEditModeManager({
    componentId,
    callbackToHide: () => {
      setShowEdit(false);
      hideImageLinkEdit();
    },
    optionalConditionToHide: canEdit || isImageLinkEditVisible,
    dependencies: [canEdit, isImageLinkEditVisible]
  });

  const updateImageHeight = (type) => {
    const height = getImageSize(imageType, type);

    if (style.maxHeight === height) {
      return;
    }

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          style: {
            ...style,
            maxHeight: height
          }
        }
      }
    });
  };

  const setSmallSizeImage = () => {
    setSmallImageSize(true);
    setMediumImageSize(false);
    setLargeImageSize(false);
    updateImageHeight(ImageSizes.SMALL);
    hideImageLinkEdit();
  };

  const setMediumSizeImage = () => {
    setSmallImageSize(false);
    setMediumImageSize(true);
    setLargeImageSize(false);
    updateImageHeight(ImageSizes.MEDIUM);
    hideImageLinkEdit();
  };

  const setLargeSizeImage = () => {
    setSmallImageSize(false);
    setMediumImageSize(false);
    setLargeImageSize(true);
    updateImageHeight(ImageSizes.LARGE);
    hideImageLinkEdit();
  };

  useEffect(() => {
    if (!imageType) {
      return;
    }

    const imageSize = getImageSizeType(imageType, style.maxHeight) || ImageSizes.MEDIUM;

    if (imageSize === ImageSizes.SMALL) {
      setSmallSizeImage();
    }

    if (imageSize === ImageSizes.MEDIUM) {
      setMediumSizeImage();
    }

    if (imageSize === ImageSizes.LARGE) {
      setLargeSizeImage();
    }
  }, [imageType]);

  const setIsVisible = () => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        overrides: {
          isVisible: !isVisible
        }
      }
    });
    hideImageLinkEdit();
  };

  const onChange = ({ isLoading: hasAssetUploaded, assetId: newAssetId }) => {
    setIsLoading(hasAssetUploaded);

    if (newAssetId) {
      setUploadedAssetId(newAssetId);
    }
  };

  const onEdit = () => {
    if (uploaderRef.current) {
      uploaderRef.current.openFileChooser();
    }

    hideImageLinkEdit();
  };

  const deleteAsset = () => {
    if (canUploadVideoImage) {
      handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
        data: {
          moduleDescriptorValueToSet: {
            video: {
              source: ''
            }
          },
          overrides: {
            type: ModuleType.VIDEO
          }
        }
      });
    } else {
      handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
        data: {
          moduleDescriptorValueToSet: {
            image: {
              source: style.margin === '0 auto' ? DEFAULT_LOGO : ''
            }
          },
          overrides: {
            type: ModuleType.IMAGE
          }
        }
      });
    }

    hideImageLinkEdit();
  };

  const onImageLoad = ({ target }) => {
    if (!isResizable) {
      return;
    }

    if (Math.floor(target.naturalHeight) !== Math.floor(target.naturalWidth)) {
      setImageType(ImageSizeTypes.RECTANGLE);
      return;
    }

    setImageType(ImageSizeTypes.SQUARE);
  };

  const handleBlockClick = (e) => {
    e.stopPropagation();

    if (isEditable) {
      showEdit();
    }

    return void 0;
  };

  const handleInputChanges = (value) => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          image: {
            ...image,
            imageLink: value
          }
        }
      }
    });

    hideImageLinkEdit();
  };

  const options = [
  ...(!hideVisibleOption ? [
  {
    action: setIsVisible,
    label:
    <Visible isVisible={isVisible} />

  }] :
  []),
  ...(isImageUploader || canUploadVideoImage ? [
  {
    action: onEdit,
    label:
    <Edit />

  },
  ...(assetId ? [
  {
    action: deleteAsset,
    label:
    <Trash />

  }] :
  [])] :
  []),

  ...(isLinkEditable && (isInternalAdmin || isWorkspaceAdminLinkEditable) ? [
  {
    action: isImageLinkEditVisible ? hideImageLinkEdit : showImageLinkEdit,
    isActive: isImageLinkEditVisible,
    label:
    <AnchorLink />

  }] :
  []),
  ...(isResizable ? [
  {
    action: setSmallSizeImage,
    isActive: isSmallImageSize,
    label:
    <Size iconSize={14} />

  },
  {
    action: setMediumSizeImage,
    isActive: isMediumImageSize,
    label:
    <Size iconSize={18} />

  },
  {
    action: setLargeSizeImage,
    isActive: isLargeImageSize,
    label:
    <Size iconSize={22} />

  }] :
  [])];


  const imageProps = { src: source, alt, style, computedStyle };

  if (isLoading) {
    return (
      <LoaderWrapper style={style}>
        <Icon name="shootstagrad" spin size={40} />
      </LoaderWrapper>);

  }

  return (
    <RelativeBlock>
      <ClickableDiv
        onClick={handleBlockClick}
        tabIndex="0"
        role="button"
        aria-pressed="false"
        onKeyDown={() => undefined}
        isVisible={isVisible}
        style={style}>

        {imageProps.src ?
        <ImageBase {...imageProps} canEdit={canEdit} onLoad={onImageLoad} /> :

        <StyledIcon
          name="image"
          color="black25"
          size={30} />}


      </ClickableDiv>
      {isEditable && canEdit &&
      <span ref={actionsRef}>
          <OptionsBlock
          options={options}
          isLinkEditVisible={isImageLinkEditVisible}
          inputData={inputData}
          handleInputChanges={handleInputChanges}
          centerAlign={style.margin === '0 auto'}
          acceptedFormats={canUploadVideoImage ? ['video/*', 'image/*'] : ['image/*']}
          filePickerRefCallBack={filePickerRefCallBack}
          onFileChange={onChange}
          showFileUploader />

        </span>}

    </RelativeBlock>);

};

export default memo(Image);